import type { MonthlyReportListItem } from "@/modules/management/monthly-report/domain/types";
import {
  Button,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import { format } from "date-fns";
import {
  Download,
  ExternalLink,
  Eye,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { getLaoMonthName } from "@/shared/lib/date-time";
import { monthlyReportApi } from "../api/client";

function formatDate(s: string) {
  try {
    return format(new Date(s), "dd/MM/yyyy");
  } catch {
    return s;
  }
}

function monthLabel(month: number): string {
  return getLaoMonthName(month);
}

type MonthlyReportTableProps = {
  data: MonthlyReportListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: MonthlyReportListItem) => void;
  onEdit: (row: MonthlyReportListItem) => void;
  onDelete?: (row: MonthlyReportListItem) => void;
};

export function MonthlyReportTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
}: MonthlyReportTableProps) {
  const columns: TanstackReactTable.ColumnDef<MonthlyReportListItem>[] = [
    createSortableColumn<MonthlyReportListItem>("periodMonth", "ເດືອນ", {
      size: 90,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {monthLabel(row.original.periodMonth)}
        </span>
      ),
    }),
    createSortableColumn<MonthlyReportListItem>("periodYear", "ປີ", {
      size: 80,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.periodYear}
        </span>
      ),
    }),
    createSortableColumn<MonthlyReportListItem>("departmentName", "ພະແນກ", {
      size: 160,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.departmentName ?? "—"}
        </span>
      ),
    }),
    {
      id: "title",
      header: "ຊື່ / ຄຳອະທິບາຍ",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-[200px] text-muted-foreground text-sm">
          {row.original.title ?? "—"}
        </span>
      ),
      size: 180,
    },
    createSortableColumn<MonthlyReportListItem>("createdByName", "ຜູ້ສ້າງ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.createdByName ?? "—"}
        </span>
      ),
    }),
    createSortableColumn<MonthlyReportListItem>("createdAt", "ວັນທີອັບໂຫຼດ", {
      size: 110,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        const hasFile = Boolean(r.fileKey);
        const hasLink = Boolean(r.documentUrl);

        const items: {
          label: string;
          onClick: () => void;
          icon?: ReactNode;
        }[] = [];

        if (onView) {
          items.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            onClick: () => onView(r),
            icon: <Eye className="h-4 w-4" />,
          });
        }
        if (hasFile) {
          items.push({
            label: "ດາວໂຫຼດເອກະສານ",
            onClick: () => {
              const url = monthlyReportApi.getFileUrl(r.fileKey!);
              window.open(url, "_blank");
            },
            icon: <Download className="h-4 w-4" />,
          });
        }
        if (hasLink) {
          items.push({
            label: "ເປີດລິ້ງ",
            onClick: () => {
              window.open(r.documentUrl!, "_blank");
            },
            icon: <ExternalLink className="h-4 w-4" />,
          });
        }
        items.push({
          label: "ແກ້ໄຂ",
          onClick: () => onEdit(r),
          icon: <Pencil className="h-4 w-4" />,
        });
        if (onDelete) {
          items.push({
            label: "ລຶບ",
            onClick: () => onDelete(r),
            icon: <Trash2 className="h-4 w-4" />,
          });
        }

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="ການກະທຳ"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {items.map((item) => {
                const isDelete = item.label === "ລຶບ";
                return (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={item.onClick}
                    className={
                      isDelete
                        ? "text-destructive focus:text-destructive"
                        : undefined
                    }
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      size: 80,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        const s = sorting[0];
        if (s?.id) onSortingChange(s.id, !!s.desc);
      }}
    />
  );
}
